import { useEffect, useRef, useState } from 'react';
import { supabase, logAudit } from './supabaseClient';

export const PAYROLL_STAGES = ['draft', 'review', 'approved', 'locked', 'paid'];
const amounts = ['basic', 'days_absent', 'ot_hours', 'hra', 'da', 'special_allowance', 'conveyance', 'medical', 'bonus', 'ot_amount', 'gross', 'pf_employee', 'pf_employer', 'esi_employee', 'esi_employer', 'professional_tax', 'absence_deduction', 'vpf', 'tds', 'net_pay', 'ctc'];
export function canonicalPayroll(rows) {
  return JSON.stringify(rows.map(row => ({ employee_id: row.employee_id, ...Object.fromEntries(amounts.map(key => [key, Number(row[key]) || 0])) }))
    .sort((a, b) => String(a.employee_id).localeCompare(String(b.employee_id))));
}
export function buildPayrollRows(employees, data, companyId, period) {
  if (!period) return [];
  const fields = { basic: 'basic', days_absent: 'absent_days', ot_hours: 'ot_hours', hra: 'hra', da: 'da', special_allowance: 'special', conveyance: 'conveyance', medical: 'medical', bonus: 'bonus', ot_amount: 'ot_pay', gross: 'gross', pf_employee: 'pf_emp', pf_employer: 'pf_er', esi_employee: 'esi_emp', esi_employer: 'esi_er', professional_tax: 'pt', absence_deduction: 'absence_deduction', vpf: 'vpf', tds: 'tds', net_pay: 'net', ctc: 'ctc' };
  return employees.filter(emp => data[emp.id]).map(emp => ({
    employee_id: emp.id, company_id: companyId, month: period.month, year: period.year,
    ...Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, Number(data[emp.id][field]) || 0])), status: 'draft',
  }));
}
export function payrollStage(metadata, rows) {
  if (rows.some(row => row.status === 'locked')) return metadata?.stage === 'paid' ? 'paid' : 'locked';
  if (metadata?.stage === 'locked' || metadata?.stage === 'paid') return 'locked'; // fail closed after a partial lock/unlock failure
  return ['review', 'approved'].includes(metadata?.stage) ? metadata.stage : 'draft';
}
export function canTransition(from, to) {
  return ({ draft: ['review'], review: ['draft', 'approved'], approved: ['draft', 'locked'], locked: ['draft', 'paid'], paid: [] }[from] || []).includes(to);
}
export async function assertPayrollDraft(companyId, period) {
  const [companyResult, salariesResult] = await Promise.all([
    supabase.from('companies').select('config').eq('id', companyId).single(),
    supabase.from('salary_records').select('status').eq('company_id', companyId).eq('month', Number(period.month)).eq('year', Number(period.year)),
  ]);
  if (companyResult.error) throw companyResult.error;
  if (salariesResult.error) throw salariesResult.error;
  const key = `${period.year}-${String(period.month).padStart(2, '0')}`;
  const metadata = companyResult.data.config?.payroll_workflows?.[key];
  if (metadata?.operation || payrollStage(metadata, salariesResult.data || []) !== 'draft') throw new Error('This payroll is under review, approved, locked or paid. Return it to draft from Run Payroll before changing salary records.');
}

async function fingerprint(rows) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalPayroll(rows)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

// Workflow metadata uses existing company JSON configuration; salary statuses remain draft/locked
// so historical payslip visibility and existing exports remain compatible.
export function usePayrollWorkflow({ companyId, period, actor, expectedRows, onConfig, onRecords, client = supabase, audit = logAudit }) {
  const [state, setState] = useState({ stage: 'draft', metadata: null, loading: true, busy: false, error: '' });
  const refs = useRef({});
  refs.current = { actor, expectedRows, onConfig, onRecords };
  const running = useRef(false);
  const scope = `${companyId || ''}:${period?.year || ''}:${period?.month || ''}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const key = period ? `${period.year}-${String(period.month).padStart(2, '0')}` : '';
  const read = async () => {
    const [companyResult, salariesResult] = await Promise.all([
      client.from('companies').select('config').eq('id', companyId).single(),
      client.from('salary_records').select('*').eq('company_id', companyId).eq('year', period.year).eq('month', period.month),
    ]);
    if (currentScope.current !== scope) throw new Error('Payroll selection changed. Retry from the selected period.');
    if (companyResult.error) throw companyResult.error;
    if (salariesResult.error) throw salariesResult.error;
    const config = companyResult.data.config;
    const metadata = config?.payroll_workflows?.[key];
    return { config, metadata, rows: salariesResult.data || [], stage: payrollStage(metadata, salariesResult.data || []) };
  };
  const publish = fresh => {
    if (currentScope.current !== scope) return;
    refs.current.onConfig?.(fresh.config || {});
    setState(previous => ({ ...previous, stage: fresh.stage, metadata: fresh.metadata || null, loading: false, error: '' }));
  };
  const refresh = async () => {
    if (!companyId || !period) return;
    try { publish(await read()); }
    catch (error) { if (currentScope.current === scope) setState(previous => ({ ...previous, loading: false, error: error.message })); }
  };
  useEffect(() => {
    if (!companyId || !period) { setState({ stage: 'draft', metadata: null, loading: false, busy: false, error: '' }); return; }
    setState({ stage: 'draft', metadata: null, loading: true, busy: false, error: '' });
    refresh();
    // Scope is the selected company and payroll period; callbacks and editable rows live in refs.
  }, [scope]);
  const perform = async operation => {
    if (running.current) return false;
    running.current = true;
    setState(previous => ({ ...previous, busy: true, error: '' }));
    try { await operation(); await refresh(); await refs.current.onRecords?.(); return true; }
    catch (error) {
      await refresh();
      if (currentScope.current === scope) setState(previous => ({ ...previous, error: error.message }));
      return false;
    } finally {
      running.current = false;
      if (currentScope.current === scope) setState(previous => ({ ...previous, busy: false }));
    }
  };
  const writeMetadata = async (fresh, metadata) => {
    if (currentScope.current !== scope) throw new Error('Payroll selection changed. Retry from the selected period.');
    const config = { ...(fresh.config || {}), payroll_workflows: { ...(fresh.config?.payroll_workflows || {}), [key]: metadata } };
    let query = client.from('companies').update({ config }).eq('id', companyId);
    query = fresh.config == null ? query.is('config', null) : query.eq('config', JSON.stringify(fresh.config));
    const { data, error } = await query.select('id');
    if (error) throw error;
    if (data?.length !== 1) throw new Error('Company settings changed in another session. Refresh and retry.');
  };
  const saveDraft = rows => perform(async () => {
    const fresh = await read();
    if (fresh.stage !== 'draft') throw new Error('Return this payroll to draft before saving changes.');
    if (fresh.metadata?.operation) throw new Error('Another payroll save is in progress. Refresh when it finishes.');
    if (!rows.length) throw new Error('There are no eligible employees to save.');
    const token = crypto.randomUUID();
    await writeMetadata(fresh, { ...(fresh.metadata || {}), stage: 'draft', operation: token, operation_at: new Date().toISOString() });
    try {
      const { error } = await client.from('salary_records').upsert(rows, { onConflict: 'employee_id,month,year', ignoreDuplicates: false });
      if (error) throw error;
    } finally {
      const latest = await read();
      if (latest.metadata?.operation === token) {
        const { operation, operation_at, ...metadata } = latest.metadata;
        await writeMetadata(latest, metadata);
      }
    }
  });
  const transition = (next, payment) => perform(async () => {
    if (!['admin', 'company'].includes(refs.current.actor?.role)) throw new Error('Administrator access is required.');
    const fresh = await read();
    if (fresh.metadata?.operation) throw new Error('Another payroll save is in progress. Refresh when it finishes.');
    if (!canTransition(fresh.stage, next)) throw new Error('This payroll stage has changed. Refresh before continuing.');
    if (!fresh.rows.length) throw new Error('Save the payroll draft before submitting it for review.');
    const snapshot = await fingerprint(fresh.rows);
    if (next !== 'draft') {
      if (canonicalPayroll(fresh.rows) !== canonicalPayroll(refs.current.expectedRows || [])) throw new Error('Payroll amounts or employees have changed. Return to draft, save, and review the updated run.');
      if (['approved', 'locked', 'paid'].includes(next) && fresh.metadata?.snapshot && snapshot !== fresh.metadata.snapshot) throw new Error('Saved payroll changed after review. Return to draft and review it again.');
      if (next === 'locked' && (!fresh.metadata?.snapshot || fresh.metadata.stage !== 'approved')) throw new Error('Approve the saved payroll before locking it.');
      if (next === 'paid' && !fresh.rows.every(row => row.status === 'locked')) throw new Error('All salary records must be locked before recording payment.');
    }
    if (next === 'paid') {
      const date = payment?.date;
      const parsed = date && new Date(`${date}T00:00:00`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !Number.isFinite(parsed?.getTime()) || parsed > new Date() || `${parsed?.getFullYear()}-${String((parsed?.getMonth() ?? 0) + 1).padStart(2, '0')}-${String(parsed?.getDate()).padStart(2, '0')}` !== date || !payment.reference?.trim()) throw new Error('Enter a valid payment date (not in the future) and a payment reference.');
    }
    const by = refs.current.actor?.username || refs.current.actor?.login_username || refs.current.actor?.role || 'Admin';
    const at = new Date().toISOString();
    const history = [...(fresh.metadata?.history || []), { from: fresh.stage, to: next, by, at }];
    const metadata = { stage: next, snapshot: next === 'draft' ? null : snapshot, history, updated_by: by, updated_at: at,
      ...(next === 'paid' ? { payment: { date: payment.date, reference: payment.reference.trim(), recorded_by: by, recorded_at: at } } : {}) };
    if (next === 'locked' || (next === 'draft' && fresh.rows.some(row => row.status === 'locked'))) {
      // Persist the lock intent first: if the salary update fails, the UI remains protected and can retry via return-to-draft.
      await writeMetadata(fresh, { ...metadata, stage: 'locked' });
      const { data, error } = await client.from('salary_records').update({ status: next === 'locked' ? 'locked' : 'draft' })
        .eq('company_id', companyId).eq('year', period.year).eq('month', period.month).in('id', fresh.rows.map(row => row.id)).select('id');
      if (error) throw error;
      if (data?.length !== fresh.rows.length) throw new Error('Not all salary records were updated. Refresh and review this payroll.');
      if (next === 'draft') await writeMetadata(await read(), metadata);
    } else await writeMetadata(fresh, metadata);
    audit(refs.current.actor, `PAYROLL_${next.toUpperCase()}`, { company_id: companyId, month: period.month, year: period.year, ...payment });
  });
  const recoverDraftSave = () => perform(async () => {
    const fresh = await read();
    if (fresh.stage !== 'draft' || !fresh.metadata?.operation || Date.now() - Date.parse(fresh.metadata.operation_at) < 300000) throw new Error('An interrupted save can be cleared after five minutes.');
    const { operation, operation_at, ...metadata } = fresh.metadata;
    await writeMetadata(fresh, metadata);
    audit(refs.current.actor, 'PAYROLL_INTERRUPTED_SAVE_CLEARED', { company_id: companyId, month: period.month, year: period.year });
  });
  return { ...state, saveDraft, transition, refresh, recoverDraftSave };

}
